/**
 * Central friendly-error copy for the Recruiter app (EU-403).
 *
 * The single place that turns an API failure into something the consultant can
 * read. It maps by HTTP status only and NEVER forwards the backend's technical
 * `message` (e.g. `FST_ERR_VALIDATION`, "body/startApplying Invalid ISO
 * datetime") — that leak is exactly what EU-403 RN-01/RN-02 fix. Unknown
 * statuses fall back to a generic message.
 *
 * EU-403 RN-04: the exact wording must follow Figma. The strings below are
 * provisional placeholders, validated with the product designer at the end of
 * the ticket.
 */

const GENERIC_ERROR = 'Não foi possível concluir a ação. Tente novamente.';
const NETWORK_ERROR = 'Não foi possível conectar. Verifique sua conexão e tente novamente.';
const SERVER_ERROR = 'Tivemos um problema por aqui. Tente novamente em instantes.';

const STATUS_MESSAGES: Record<number, string> = {
  400: GENERIC_ERROR,
  403: 'Você não tem permissão para realizar esta ação.',
  404: 'Não encontramos o que você procura.',
  409: 'Esta ação conflita com o estado atual. Atualize a página e tente novamente.',
  422: GENERIC_ERROR,
};

/**
 * Read the HTTP status off any error that carries a numeric `status` — the
 * shape every typed SDK error in this app uses (`ProgramMutationError`,
 * `JobMutationError`, `ProgramByIdFetchError`, …). `fetchClient` maps
 * network/timeout failures to `status: 0`.
 */
export function getErrorStatus(error: unknown): number | null {
  if (error && typeof error === 'object' && 'status' in error) {
    const status = (error as { status?: unknown }).status;
    if (typeof status === 'number') return status;
  }
  return null;
}

/** Map an API failure to user-facing copy, never exposing the backend message. */
export function toFriendlyMessage(error: unknown): string {
  const status = getErrorStatus(error);
  if (status == null) return GENERIC_ERROR;
  if (status === 0) return NETWORK_ERROR;
  const mapped = STATUS_MESSAGES[status];
  if (mapped) return mapped;
  if (status >= 500) return SERVER_ERROR;
  return GENERIC_ERROR;
}
